import { OpenAPI } from "@/client"
import { VisionCard } from "@/components/VisionUI"
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { QRCodeSVG } from "qrcode.react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiClock,
  FiKey,
  FiPlus,
  FiSlash,
  FiSmartphone,
  FiTrash2,
} from "react-icons/fi"

interface License {
  id: string
  key: string
  device_id: string
  mac_address: string | null
  customer_name: string | null
  customer_email: string | null
  product: string | null
  product_en: string | null
  status: string
  is_trial: boolean
  is_activated: boolean
  is_subscription: boolean
  max_tags: number
  expires_at: string | null
  created_at: string | null
  days_remaining?: number
}

interface LicenseRequest {
  id: string
  device_id: string
  mac_address: string | null
  email: string | null
  name: string | null
  product: string | null
  app_version: string | null
  reader_model: string | null
  tier: string | null
  requested_max_tags: number | null
  status: string
  note: string | null
  license_id: string | null
  created_at: string | null
  is_new: boolean
  previous_license_key: string | null
  previous_license_status: string | null
}

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = localStorage.getItem("access_token")
  return fetch(`${OpenAPI.BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
}

// QR payload QDatDroid scans to install a license. Mirrors the reader-config
// envelope ({type, version, ...}); api_base tells the app which backend to
// validate/sync against.
function buildLicenseQr(lic: License): string {
  return JSON.stringify({
    type: "qdat-license",
    version: "1.0",
    license: {
      key: lic.key,
      product: lic.product || "QDatDroid",
      api_base: OpenAPI.BASE,
    },
  })
}

export const Route = createFileRoute("/_layout/licenses")({
  component: LicensesPage,
})

function LicensesPage() {
  const { t } = useTranslation(["admin", "common"])
  const sc = useSemanticColors()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const [deviceId, setDeviceId] = useState("")
  const [customerName, setCustomerName] = useState("")
  const [customerEmail, setCustomerEmail] = useState("")
  const [product, setProduct] = useState("QDatDroid")
  const [maxTags, setMaxTags] = useState("0")
  const [validDays, setValidDays] = useState("")
  const [isSubscription, setIsSubscription] = useState(false)
  const [isActivated, setIsActivated] = useState(true)

  // QR modal (shown for an existing license or right after mint/approve).
  const [qrLicense, setQrLicense] = useState<License | null>(null)

  // Bulk selection of revoked licenses for deletion.
  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Approve modal state (per pending request).
  const [approving, setApproving] = useState<LicenseRequest | null>(null)
  const [approveMaxTags, setApproveMaxTags] = useState("0")
  const [approveValidDays, setApproveValidDays] = useState("")
  const [approveSubscription, setApproveSubscription] = useState(false)

  const { data: licenses, isLoading } = useQuery({
    queryKey: ["licenses"],
    queryFn: async () => {
      const res = await apiFetch("/api/v1/licenses/")
      if (!res.ok) throw new Error("Failed to load licenses")
      return res.json() as Promise<{ data: License[]; count: number }>
    },
  })

  const { data: requests } = useQuery({
    queryKey: ["license-requests", "pending"],
    queryFn: async () => {
      const res = await apiFetch(
        "/api/v1/licenses/requests?status_filter=pending",
      )
      if (!res.ok) throw new Error("Failed to load license requests")
      return res.json() as Promise<{ data: LicenseRequest[]; count: number }>
    },
    refetchInterval: 15000,
  })

  const mintMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch("/api/v1/licenses/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_id: deviceId,
          customer_name: customerName || null,
          customer_email: customerEmail || null,
          product: product || null,
          product_en: product || null,
          max_tags: Number(maxTags) || 0,
          valid_days: validDays === "" ? null : Number(validDays),
          is_subscription: isSubscription,
          is_activated: isActivated,
          is_trial: false,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || "Failed to mint license")
      }
      return res.json() as Promise<License>
    },
    onSuccess: (lic) => {
      showSuccessToast(t("common:toast.successTitle"))
      queryClient.invalidateQueries({ queryKey: ["licenses"] })
      setDeviceId("")
      setCustomerName("")
      setCustomerEmail("")
      setMaxTags("0")
      setValidDays("")
      setQrLicense(lic)
    },
    onError: (e: Error) => showErrorToast(e.message),
  })

  const patchMutation = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string
      body: Record<string, unknown>
    }) => {
      const res = await apiFetch(`/api/v1/licenses/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || "Failed to update license")
      }
      return res.json()
    },
    onSuccess: () => {
      showSuccessToast(t("common:toast.successTitle"))
      queryClient.invalidateQueries({ queryKey: ["licenses"] })
    },
    onError: (e: Error) => showErrorToast(e.message),
  })

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!approving) throw new Error("No request selected")
      const res = await apiFetch(
        `/api/v1/licenses/requests/${approving.id}/approve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            max_tags: Number(approveMaxTags) || 0,
            valid_days:
              approveValidDays === "" ? null : Number(approveValidDays),
            is_subscription: approveSubscription,
          }),
        },
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || "Failed to approve request")
      }
      return res.json() as Promise<License>
    },
    onSuccess: (lic) => {
      showSuccessToast(t("common:toast.successTitle"))
      queryClient.invalidateQueries({ queryKey: ["licenses"] })
      queryClient.invalidateQueries({ queryKey: ["license-requests"] })
      setApproving(null)
      setApproveMaxTags("0")
      setApproveValidDays("")
      setApproveSubscription(false)
      setQrLicense(lic)
    },
    onError: (e: Error) => showErrorToast(e.message),
  })

  const rejectMutation = useMutation({
    mutationFn: async (req: LicenseRequest) => {
      const res = await apiFetch(`/api/v1/licenses/requests/${req.id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: null }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || "Failed to reject request")
      }
      return res.json()
    },
    onSuccess: () => {
      showSuccessToast(t("common:toast.successTitle"))
      queryClient.invalidateQueries({ queryKey: ["license-requests"] })
    },
    onError: (e: Error) => showErrorToast(e.message),
  })

  // Delete revoked licenses (single or bulk). Backend rejects non-revoked.
  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        const res = await apiFetch(`/api/v1/licenses/${id}`, {
          method: "DELETE",
        })
        if (!res.ok && res.status !== 204) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err.detail || "Failed to delete license")
        }
      }
    },
    onSuccess: () => {
      showSuccessToast(t("common:toast.successTitle"))
      setSelected(new Set())
      queryClient.invalidateQueries({ queryKey: ["licenses"] })
    },
    onError: (e: Error) => showErrorToast(e.message),
  })

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const formatExpiry = (lic: License) => {
    if (lic.expires_at === null) return t("admin:licenses.perpetual")
    return new Date(lic.expires_at).toLocaleDateString()
  }

  const formatCreated = (lic: License) =>
    lic.created_at ? new Date(lic.created_at).toLocaleDateString() : "—"

  const openApprove = (req: LicenseRequest) => {
    setApproving(req)
    setApproveMaxTags(
      req.requested_max_tags != null ? String(req.requested_max_tags) : "0",
    )
    setApproveValidDays("")
    setApproveSubscription(false)
  }

  const pending = requests?.data ?? []

  return (
    <Box p={{ base: 4, md: 8 }}>
      <Heading size="lg" color={sc.heading} mb={2}>
        {t("admin:licenses.title")}
      </Heading>
      <Text color={sc.textMuted} mb={6} maxW="3xl">
        {t("admin:licenses.subtitle")}
      </Text>

      {/* Pending device requests */}
      <VisionCard p={6} mb={6}>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("admin:licenses.pendingTitle")}{" "}
          {pending.length > 0 && (
            <Badge colorPalette="orange" ml={2}>
              {pending.length}
            </Badge>
          )}
        </Heading>
        {pending.length > 0 ? (
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader color={sc.textMuted}>
                  {t("admin:licenses.colDevice")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.textMuted}>
                  {t("admin:licenses.colType")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.textMuted}>
                  {t("admin:licenses.colCustomer")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.textMuted}>
                  {t("admin:licenses.colAppVersion")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.textMuted}>
                  {t("admin:licenses.colReader")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.textMuted}>
                  {t("admin:licenses.colRevokedLicense")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.textMuted} textAlign="end">
                  {t("admin:licenses.colRequested")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.textMuted}>
                  {t("admin:licenses.colRequestedAt")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.textMuted} textAlign="end">
                  {t("common:table.actions")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {pending.map((req) => (
                <Table.Row key={req.id}>
                  <Table.Cell color={sc.textMuted} fontFamily="mono">
                    {req.device_id}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette={req.is_new ? "green" : "purple"}>
                      {req.is_new
                        ? t("admin:licenses.typeNew")
                        : t("admin:licenses.typeRenew")}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell color={sc.textMuted}>
                    {req.name || req.email || "—"}
                  </Table.Cell>
                  <Table.Cell color={sc.textMuted}>
                    {req.app_version || "—"}
                  </Table.Cell>
                  <Table.Cell color={sc.textMuted}>
                    {req.reader_model || "—"}
                  </Table.Cell>
                  <Table.Cell color={sc.textMuted} fontFamily="mono">
                    {req.previous_license_key ? (
                      <>
                        {req.previous_license_key}
                        {req.previous_license_status && (
                          <Badge
                            ml={2}
                            colorPalette={
                              req.previous_license_status === "revoked"
                                ? "red"
                                : "gray"
                            }
                          >
                            {req.previous_license_status}
                          </Badge>
                        )}
                      </>
                    ) : (
                      "—"
                    )}
                  </Table.Cell>
                  <Table.Cell color={sc.textMuted} textAlign="end">
                    {req.requested_max_tags ?? "—"}
                  </Table.Cell>
                  <Table.Cell color={sc.textMuted}>
                    {req.created_at
                      ? new Date(req.created_at).toLocaleString()
                      : "—"}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    <Button
                      size="xs"
                      colorPalette="green"
                      mr={2}
                      onClick={() => openApprove(req)}
                    >
                      <FiKey /> {t("admin:licenses.approve")}
                    </Button>
                    <Button
                      size="xs"
                      colorPalette="blue"
                      variant="outline"
                      mr={2}
                      onClick={() =>
                        navigate({
                          to: "/reader-form",
                          search: { requestId: req.id },
                        })
                      }
                    >
                      <FiSmartphone /> {t("admin:licenses.provision")}
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorPalette="red"
                      onClick={() => rejectMutation.mutate(req)}
                    >
                      <FiSlash /> {t("admin:licenses.reject")}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        ) : (
          <Text color={sc.textMuted}>{t("admin:licenses.noRequests")}</Text>
        )}
      </VisionCard>

      {/* Mint a license by hand */}
      <VisionCard p={6} mb={6}>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("admin:licenses.mintTitle")}
        </Heading>
        <VStack align="stretch" gap={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <Box>
              <Text mb={2} color={sc.text}>
                {t("admin:licenses.deviceId")}
              </Text>
              <Input
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                bg={sc.pageBg}
                color={sc.text}
              />
            </Box>
            <Box>
              <Text mb={2} color={sc.text}>
                {t("admin:licenses.product")}
              </Text>
              <Input
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                bg={sc.pageBg}
                color={sc.text}
              />
            </Box>
            <Box>
              <Text mb={2} color={sc.text}>
                {t("admin:licenses.customerName")}
              </Text>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                bg={sc.pageBg}
                color={sc.text}
              />
            </Box>
            <Box>
              <Text mb={2} color={sc.text}>
                {t("admin:licenses.customerEmail")}
              </Text>
              <Input
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                bg={sc.pageBg}
                color={sc.text}
              />
            </Box>
            <Box>
              <Text mb={2} color={sc.text}>
                {t("admin:licenses.maxTags")}
              </Text>
              <Input
                type="number"
                value={maxTags}
                onChange={(e) => setMaxTags(e.target.value)}
                bg={sc.pageBg}
                color={sc.text}
              />
            </Box>
            <Box>
              <Text mb={2} color={sc.text}>
                {t("admin:licenses.validDays")}
              </Text>
              <Input
                type="number"
                value={validDays}
                onChange={(e) => setValidDays(e.target.value)}
                bg={sc.pageBg}
                color={sc.text}
              />
            </Box>
          </SimpleGrid>
          <Checkbox.Root
            checked={isActivated}
            onCheckedChange={(d) => setIsActivated(!!d.checked)}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label color={sc.text}>
              {t("admin:licenses.isActivated")}
            </Checkbox.Label>
          </Checkbox.Root>
          <Checkbox.Root
            checked={isSubscription}
            onCheckedChange={(d) => setIsSubscription(!!d.checked)}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label color={sc.text}>
              {t("admin:licenses.isSubscription")}
            </Checkbox.Label>
          </Checkbox.Root>
          <Button
            onClick={() => mintMutation.mutate()}
            disabled={!deviceId || mintMutation.isPending}
            colorPalette="blue"
            alignSelf="flex-start"
          >
            <FiPlus /> {t("admin:licenses.mint")}
          </Button>
        </VStack>
      </VisionCard>

      {/* Existing licenses */}
      {isLoading ? (
        <Spinner color={sc.spinnerColor} />
      ) : (
        <VisionCard p={6}>
          {selected.size > 0 && (
            <Button
              size="sm"
              colorPalette="red"
              mb={4}
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate([...selected])}
            >
              <FiTrash2 />{" "}
              {t("admin:licenses.deleteSelected", { count: selected.size })}
            </Button>
          )}
          {licenses && licenses.data.length > 0 ? (
            <Box overflowX="auto">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader />
                    <Table.ColumnHeader color={sc.textMuted}>
                      {t("admin:licenses.colKey")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted}>
                      {t("admin:licenses.colDevice")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted}>
                      {t("admin:licenses.colCustomer")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted}>
                      {t("admin:licenses.colEmail")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted}>
                      {t("admin:licenses.colStatus")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted} textAlign="end">
                      {t("admin:licenses.colTags")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted}>
                      {t("admin:licenses.colCreated")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted} textAlign="end">
                      {t("admin:licenses.colDays")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted}>
                      {t("admin:licenses.colExpires")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted}>
                      {t("admin:licenses.colTrial")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={sc.textMuted} textAlign="end">
                      {t("common:table.actions")}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {licenses.data.map((lic) => (
                    <Table.Row key={lic.id}>
                      <Table.Cell>
                        {lic.status === "revoked" && (
                          <Checkbox.Root
                            size="sm"
                            checked={selected.has(lic.id)}
                            onCheckedChange={() => toggleSelected(lic.id)}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                          </Checkbox.Root>
                        )}
                      </Table.Cell>
                      <Table.Cell color={sc.text} fontFamily="mono">
                        {lic.key}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted}>
                        {lic.device_id}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted}>
                        {lic.customer_name || "—"}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted}>
                        {lic.customer_email || "—"}
                      </Table.Cell>
                      <Table.Cell color={sc.text}>{lic.status}</Table.Cell>
                      <Table.Cell color={sc.textMuted} textAlign="end">
                        {lic.max_tags}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted}>
                        {formatCreated(lic)}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted} textAlign="end">
                        {lic.days_remaining ?? "—"}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted}>
                        {formatExpiry(lic)}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted}>
                        {lic.is_trial ? "✓" : "—"}
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        <Button
                          size="xs"
                          variant="ghost"
                          mr={2}
                          onClick={() => setQrLicense(lic)}
                        >
                          <FiKey /> {t("admin:licenses.showQr")}
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          mr={2}
                          onClick={() =>
                            patchMutation.mutate({
                              id: lic.id,
                              body: { extend_days: 30 },
                            })
                          }
                        >
                          <FiClock /> {t("admin:licenses.extend30")}
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          disabled={lic.status === "revoked"}
                          onClick={() =>
                            patchMutation.mutate({
                              id: lic.id,
                              body: { status: "revoked" },
                            })
                          }
                        >
                          <FiSlash /> {t("admin:licenses.revoke")}
                        </Button>
                        {lic.status === "revoked" && (
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="red"
                            disabled={deleteMutation.isPending}
                            onClick={() => deleteMutation.mutate([lic.id])}
                          >
                            <FiTrash2 /> {t("admin:licenses.delete")}
                          </Button>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          ) : (
            <Text color={sc.textMuted}>{t("admin:licenses.noLicenses")}</Text>
          )}
        </VisionCard>
      )}

      {/* Approve modal */}
      <DialogRoot
        open={!!approving}
        onOpenChange={(e) => {
          if (!e.open) setApproving(null)
        }}
      >
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>
            <DialogTitle color={sc.heading}>
              {t("admin:licenses.approveTitle")}
            </DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <VStack align="stretch" gap={4} pb={4}>
              <Text color={sc.textMuted} fontFamily="mono" fontSize="sm">
                {approving?.device_id}
              </Text>
              <Box>
                <Text mb={2} color={sc.text}>
                  {t("admin:licenses.maxTags")}
                </Text>
                <Input
                  type="number"
                  value={approveMaxTags}
                  onChange={(e) => setApproveMaxTags(e.target.value)}
                  bg={sc.pageBg}
                  color={sc.text}
                />
              </Box>
              <Box>
                <Text mb={2} color={sc.text}>
                  {t("admin:licenses.validDays")}
                </Text>
                <Input
                  type="number"
                  value={approveValidDays}
                  onChange={(e) => setApproveValidDays(e.target.value)}
                  bg={sc.pageBg}
                  color={sc.text}
                />
              </Box>
              <Checkbox.Root
                checked={approveSubscription}
                onCheckedChange={(d) => setApproveSubscription(!!d.checked)}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label color={sc.text}>
                  {t("admin:licenses.isSubscription")}
                </Checkbox.Label>
              </Checkbox.Root>
              <Button
                colorPalette="green"
                disabled={approveMutation.isPending}
                onClick={() => approveMutation.mutate()}
              >
                <FiKey /> {t("admin:licenses.confirmApprove")}
              </Button>
            </VStack>
          </DialogBody>
        </DialogContent>
      </DialogRoot>

      {/* QR modal */}
      <DialogRoot
        open={!!qrLicense}
        onOpenChange={(e) => {
          if (!e.open) setQrLicense(null)
        }}
      >
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>
            <DialogTitle color={sc.heading}>
              {t("admin:licenses.qrTitle")}
            </DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <VStack align="center" gap={4} pb={6}>
              {qrLicense && (
                <Box bg="white" p={4} borderRadius="md">
                  <QRCodeSVG
                    value={buildLicenseQr(qrLicense)}
                    size={256}
                    level="M"
                  />
                </Box>
              )}
              <Text color={sc.text} fontFamily="mono" fontWeight="bold">
                {qrLicense?.key}
              </Text>
              <Text color={sc.textMuted} fontSize="sm" textAlign="center">
                {t("admin:licenses.qrHint")}
              </Text>
            </VStack>
          </DialogBody>
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}
